/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  ConnectionErrorReason,
  DisconnectReason,
  type ConnectionError,
} from "livekit-client";

import type {
  CallRecoveryCancellationCode,
  FailureClassification,
  FailureClassificationReason,
  FailureContext,
  TerminalCallErrorCode,
} from "./CallRecoveryTypes";

const RETRYABLE_HTTP_STATUSES = new Set([429]);
const RETRYABLE_NETWORK_CODES = new Set([
  "EAI_AGAIN",
  "ECONNABORTED",
  "ECONNREFUSED",
  "ECONNRESET",
  "ENETDOWN",
  "ENETUNREACH",
  "ENOTFOUND",
  "ETIMEDOUT",
  "ERR_NETWORK",
  "NETWORK_ERROR",
]);
const AUTH_ERRCODES = new Set(["M_MISSING_TOKEN", "M_UNKNOWN_TOKEN"]);
const FORBIDDEN_ERRCODES = new Set(["M_FORBIDDEN"]);
const NOT_FOUND_ERRCODES = new Set(["M_NOT_FOUND"]);
const RETRYABLE_ERRCODES = new Set(["M_LIMIT_EXCEEDED"]);
const ELEMENT_CALL_CODES = new Set([
  "CONNECTION_LOST_ERROR",
  "E2EE_NOT_SUPPORTED",
  "INSUFFICIENT_CAPACITY_ERROR",
  "MISSING_MATRIX_RTC_FOCUS",
  "OPEN_ID_ERROR",
  "SFU_ERROR",
]);

interface ErrorFacts {
  readonly name?: string;
  readonly code?: string;
  readonly elementCallCode?: string;
  readonly networkCode?: string;
  readonly httpStatus?: number;
  readonly matrixErrcode?: string;
  readonly livekitReason?: ConnectionErrorReason;
  readonly livekitContext?: DisconnectReason;
  readonly isAbort: boolean;
  readonly isDeviceUnsupported: boolean;
  readonly isLivekitConnectionError: boolean;
  readonly isTokenRefreshLogoutError: boolean;
  readonly isTypeError: boolean;
  readonly isUnsupportedServer: boolean;
}

/**
 * Строит сигнатуру, не зависящую от текста, stack trace и identity ошибки.
 */
export function createStableFailureSignature(
  context: Pick<FailureContext, "stage" | "subsystem" | "operation">,
  normalizedCode: string,
): string {
  return [
    ["stage", context.stage],
    ["subsystem", context.subsystem],
    ["operation", context.operation],
    ["code", normalizedCode],
  ]
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("|");
}

/**
 * Классифицирует одно падение recovery pipeline без обращения к browser globals.
 */
export function classifyCallRecoveryFailure(
  error: unknown,
  context: FailureContext,
): FailureClassification {
  const facts = collectErrorFacts(error);

  const cancellationCode = getCancellationCode(facts, context);
  if (cancellationCode !== undefined) {
    return cancelled(
      error,
      context,
      `cancelled:${cancellationCode}`,
      cancellationCode,
    );
  }

  const retryableHttp =
    isRetryableHttpStatus(facts.httpStatus) || isRetryableLivekitStatus(facts);
  const typedDisconnectReason =
    context.livekitDisconnectReason ?? facts.livekitContext;
  const contextualHttpClassification = classifyContextualHttp(
    error,
    context,
    facts,
  );
  const elementCallClassification = classifyElementCallCode(
    error,
    context,
    facts.elementCallCode,
  );
  const livekitClassification = classifyLivekit(error, context, facts);

  // Typed disconnect приходит от уже разобранного LiveKit protocol event и
  // потому остаётся доказательством terminal даже при одновременном offline.
  if (
    typedDisconnectReason !== undefined &&
    livekitClassification?.kind === "terminal"
  ) {
    return livekitClassification;
  }

  // Временный HTTP status сильнее неоднозначных terminal hints из тела ошибки:
  // 429/5xx обязаны пройти обычную retry policy.
  if (!retryableHttp) {
    const explicitTerminal = classifyExplicitTerminalHint(error, context);
    if (explicitTerminal !== undefined) return explicitTerminal;

    if (facts.name === "NotAllowedError") {
      return terminal(
        error,
        context,
        "media:not_allowed",
        "permission_denied",
        "livekit_not_allowed",
      );
    }

    const matrixAuthentication = classifyMatrixAuthentication(
      error,
      context,
      facts,
    );
    if (matrixAuthentication !== undefined) return matrixAuthentication;

    if (contextualHttpClassification?.kind === "terminal") {
      return contextualHttpClassification;
    }
    if (elementCallClassification?.kind === "terminal") {
      return elementCallClassification;
    }
    if (
      livekitClassification?.kind === "terminal" ||
      livekitClassification?.kind === "cancelled"
    ) {
      return livekitClassification;
    }
  }

  // Offline применяется только после проверки intrinsic terminal evidence.
  if (context.networkState === "offline") {
    return classified(
      "wait_for_network",
      error,
      context,
      "network:offline",
      "browser_offline",
    );
  }

  if (retryableHttp) {
    return classified(
      "retryable",
      error,
      context,
      `http:${facts.httpStatus}`,
      "retryable_http_status",
    );
  }

  if (contextualHttpClassification !== undefined) {
    return contextualHttpClassification;
  }
  if (elementCallClassification !== undefined) {
    return elementCallClassification;
  }
  if (livekitClassification !== undefined) {
    return livekitClassification;
  }

  if (
    facts.matrixErrcode !== undefined &&
    RETRYABLE_ERRCODES.has(facts.matrixErrcode)
  ) {
    return classified(
      "retryable",
      error,
      context,
      `matrix:${normalizeToken(facts.matrixErrcode)}`,
      "retryable_matrix_errcode",
    );
  }

  if (isTransportFailure(facts)) {
    return classified(
      "retryable",
      error,
      context,
      normalizeTransportCode(facts),
      "transport_failure",
    );
  }

  if (context.stage === "matrix_sync" || context.retryableHint !== undefined) {
    const hint = context.retryableHint ?? "matrix_sync_failure";
    return classified("retryable", error, context, `retryable:${hint}`, hint);
  }

  const normalizedCode = normalizeUnknownCode(facts, error);
  if (context.consecutiveSameFailureCount >= 5) {
    return terminal(
      error,
      context,
      normalizedCode,
      "repeated_unknown_failure",
      "unknown_failure_threshold_reached",
    );
  }

  return classified(
    "retryable",
    error,
    context,
    normalizedCode,
    "unknown_failure_below_threshold",
  );
}

function classifyExplicitTerminalHint(
  error: unknown,
  context: FailureContext,
): FailureClassification | undefined {
  if (context.terminalHint === undefined) return undefined;
  const normalizedCode = context.terminalHint.serverCode
    ? `terminal_server:${normalizeToken(context.terminalHint.serverCode)}`
    : `terminal:${context.terminalHint.code}`;
  return terminal(
    error,
    context,
    normalizedCode,
    context.terminalHint.code,
    "explicit_terminal_hint",
  );
}

function classifyMatrixAuthentication(
  error: unknown,
  context: FailureContext,
  facts: ErrorFacts,
): FailureClassification | undefined {
  if (
    !isMatrixAuthenticationContext(context) ||
    (facts.httpStatus !== 401 &&
      !facts.isTokenRefreshLogoutError &&
      (facts.matrixErrcode === undefined ||
        !AUTH_ERRCODES.has(facts.matrixErrcode)))
  ) {
    return undefined;
  }
  const normalizedCode =
    facts.matrixErrcode === undefined
      ? facts.isTokenRefreshLogoutError
        ? "matrix:token_refresh_logout"
        : "http:401"
      : `matrix:${normalizeToken(facts.matrixErrcode)}`;
  return terminal(
    error,
    context,
    normalizedCode,
    "auth_revoked",
    "matrix_auth_revoked",
  );
}

function classifyElementCallCode(
  error: unknown,
  context: FailureContext,
  code: string | undefined,
): FailureClassification | undefined {
  const normalizedCode = `element_call:${normalizeToken(code ?? "unknown")}`;
  switch (code) {
    case "CONNECTION_LOST_ERROR":
      return classified(
        "retryable",
        error,
        context,
        normalizedCode,
        "element_call_connection_lost",
      );
    case "OPEN_ID_ERROR":
      return classified(
        "retryable",
        error,
        context,
        normalizedCode,
        "element_call_openid_unavailable",
      );
    case "INSUFFICIENT_CAPACITY_ERROR":
      return classified(
        "retryable",
        error,
        context,
        normalizedCode,
        "element_call_insufficient_capacity",
      );
    case "MISSING_MATRIX_RTC_FOCUS":
    case "SFU_ERROR":
      return terminal(
        error,
        context,
        normalizedCode,
        "incompatible_configuration",
        "element_call_terminal_error",
      );
    case "E2EE_NOT_SUPPORTED":
      return terminal(
        error,
        context,
        normalizedCode,
        "irrecoverable_e2ee",
        "element_call_terminal_error",
      );
    default:
      return undefined;
  }
}

function classifyContextualHttp(
  error: unknown,
  context: FailureContext,
  facts: ErrorFacts,
): FailureClassification | undefined {
  const { httpStatus, matrixErrcode } = facts;
  const normalizedCode =
    matrixErrcode === undefined
      ? httpStatus === undefined
        ? undefined
        : `http:${httpStatus}`
      : `matrix:${normalizeToken(matrixErrcode)}`;

  if (context.stage === "focus") {
    if (
      httpStatus === 404 ||
      (matrixErrcode !== undefined && NOT_FOUND_ERRCODES.has(matrixErrcode))
    ) {
      return classified(
        "retryable",
        error,
        context,
        normalizedCode ?? "focus:call_not_found",
        "focus_call_not_found",
      );
    }
  }

  if (isMatrixRoomOperation(context)) {
    if (
      httpStatus === 401 ||
      (matrixErrcode !== undefined && AUTH_ERRCODES.has(matrixErrcode))
    ) {
      return terminal(
        error,
        context,
        normalizedCode ?? "matrix:auth_revoked",
        "auth_revoked",
        "room_auth_revoked",
      );
    }
    if (
      httpStatus === 403 ||
      (matrixErrcode !== undefined && FORBIDDEN_ERRCODES.has(matrixErrcode))
    ) {
      return terminal(
        error,
        context,
        normalizedCode ?? "matrix:forbidden",
        "access_forbidden",
        "room_access_forbidden",
      );
    }
    if (
      httpStatus === 404 ||
      (matrixErrcode !== undefined && NOT_FOUND_ERRCODES.has(matrixErrcode))
    ) {
      return terminal(
        error,
        context,
        normalizedCode ?? "matrix:room_unavailable",
        "room_unavailable",
        "room_unavailable",
      );
    }
  }

  if (context.stage === "credentials" || context.stage === "livekit") {
    if (
      httpStatus === 401 ||
      (matrixErrcode !== undefined && AUTH_ERRCODES.has(matrixErrcode))
    ) {
      return terminal(
        error,
        context,
        normalizedCode ?? "credentials:auth_revoked",
        "auth_revoked",
        "credentials_auth_revoked",
      );
    }
    if (
      httpStatus === 403 ||
      (matrixErrcode !== undefined && FORBIDDEN_ERRCODES.has(matrixErrcode))
    ) {
      return terminal(
        error,
        context,
        normalizedCode ?? "credentials:permission_denied",
        "permission_denied",
        "credentials_permission_denied",
      );
    }
    if (httpStatus === 404) {
      return terminal(
        error,
        context,
        normalizedCode ?? "credentials:configuration_missing",
        "incompatible_configuration",
        "terminal_http_response",
      );
    }
    if (httpStatus !== undefined && httpStatus >= 400 && httpStatus <= 499) {
      // 429 отфильтрован раньше общей contextual классификации. Остальные
      // 4xx являются устойчивым ответом сервера, а не transport-сбоем.
      return terminal(
        error,
        context,
        normalizedCode ?? `http:${httpStatus}`,
        "terminal_server_error",
        "terminal_http_response",
      );
    }
  }

  return undefined;
}

function isMatrixAuthenticationContext(context: FailureContext): boolean {
  return context.subsystem === "matrix" || isMatrixRoomOperation(context);
}

function isMatrixRoomOperation(context: FailureContext): boolean {
  if (context.subsystem !== "matrix" && context.subsystem !== "membership") {
    return false;
  }
  return (
    context.stage === "room_access" ||
    context.stage === "matrix_state" ||
    context.stage === "own_membership"
  );
}

function classifyLivekit(
  error: unknown,
  context: FailureContext,
  facts: ErrorFacts,
): FailureClassification | undefined {
  const disconnectReason =
    context.livekitDisconnectReason ?? facts.livekitContext;
  if (disconnectReason !== undefined) {
    const byDisconnectReason = classifyLivekitDisconnectReason(
      error,
      context,
      disconnectReason,
    );
    if (byDisconnectReason !== undefined) return byDisconnectReason;
  }

  if (facts.isDeviceUnsupported) {
    return terminal(
      error,
      context,
      "livekit:device_unsupported",
      "unsupported_webrtc",
      "livekit_device_unsupported",
    );
  }
  if (facts.isUnsupportedServer) {
    return terminal(
      error,
      context,
      "livekit:unsupported_server",
      "incompatible_configuration",
      "livekit_unsupported_server",
    );
  }

  switch (facts.livekitReason) {
    case ConnectionErrorReason.Cancelled:
      return cancelled(
        error,
        context,
        "livekit:cancelled",
        "operation_aborted",
      );
    case ConnectionErrorReason.NotAllowed:
      return terminal(
        error,
        context,
        "livekit:not_allowed",
        "permission_denied",
        "livekit_not_allowed",
      );
    case ConnectionErrorReason.ServerUnreachable:
    case ConnectionErrorReason.InternalError:
    case ConnectionErrorReason.Timeout:
      return classified(
        "retryable",
        error,
        context,
        `livekit:${normalizeLivekitReason(facts.livekitReason)}`,
        "livekit_temporary_failure",
      );
    case ConnectionErrorReason.LeaveRequest:
      return classified(
        "retryable",
        error,
        context,
        "livekit:unexpected_leave_request",
        "livekit_unexpected_leave",
      );
    default:
      return undefined;
  }
}

function classifyLivekitDisconnectReason(
  error: unknown,
  context: FailureContext,
  reason: DisconnectReason,
): FailureClassification | undefined {
  const normalizedCode = `livekit_disconnect:${normalizeDisconnectReason(reason)}`;
  switch (reason) {
    case DisconnectReason.PARTICIPANT_REMOVED:
      return terminal(
        error,
        context,
        normalizedCode,
        "permission_denied",
        "livekit_participant_removed",
      );
    case DisconnectReason.ROOM_DELETED:
      return terminal(
        error,
        context,
        normalizedCode,
        "room_unavailable",
        "livekit_room_deleted",
      );
    case DisconnectReason.USER_REJECTED:
      return terminal(
        error,
        context,
        normalizedCode,
        "permission_denied",
        "livekit_user_rejected",
      );
    case DisconnectReason.DUPLICATE_IDENTITY:
      // LiveKit документирует это как вытеснение текущего клиента другим
      // участником с тем же identity. Новый технический connect только
      // продолжил бы гонку двух клиентов, поэтому это terminal одной session.
      return terminal(
        error,
        context,
        normalizedCode,
        "permission_denied",
        "livekit_duplicate_identity",
      );
    case DisconnectReason.SERVER_SHUTDOWN:
    case DisconnectReason.STATE_MISMATCH:
    case DisconnectReason.JOIN_FAILURE:
    case DisconnectReason.MIGRATION:
    case DisconnectReason.SIGNAL_CLOSE:
    case DisconnectReason.ROOM_CLOSED:
    case DisconnectReason.CONNECTION_TIMEOUT:
    case DisconnectReason.MEDIA_FAILURE:
      return classified(
        "retryable",
        error,
        context,
        normalizedCode,
        "livekit_temporary_disconnect",
      );
    case DisconnectReason.CLIENT_INITIATED:
    case DisconnectReason.UNKNOWN_REASON:
    case DisconnectReason.USER_UNAVAILABLE:
    case DisconnectReason.SIP_TRUNK_FAILURE:
      return classified(
        "retryable",
        error,
        context,
        normalizedCode,
        "livekit_unexpected_disconnect",
      );
    default:
      return undefined;
  }
}

function getCancellationCode(
  facts: ErrorFacts,
  context: FailureContext,
): CallRecoveryCancellationCode | undefined {
  if (context.cancellationReason !== undefined) {
    return context.cancellationReason;
  }
  if (
    facts.isAbort ||
    facts.livekitReason === ConnectionErrorReason.Cancelled
  ) {
    return "operation_aborted";
  }
  return undefined;
}

function collectErrorFacts(error: unknown): ErrorFacts {
  const chain = collectCauseChain(error);
  return {
    name: firstString(chain, "name"),
    code: firstString(chain, "code"),
    elementCallCode: firstMatchingString(chain, "code", ELEMENT_CALL_CODES),
    networkCode: firstMatchingString(chain, "code", RETRYABLE_NETWORK_CODES),
    httpStatus:
      firstNumber(chain, "httpStatus") ?? firstNumber(chain, "status"),
    matrixErrcode:
      firstString(chain, "errcode") ??
      firstNestedString(chain, "data", "errcode"),
    livekitReason: firstLivekitReason(chain),
    livekitContext: firstLivekitContext(chain),
    isAbort: chain.some((item) => {
      const name = readString(item, "name");
      return (
        name === "AbortError" ||
        name === "CanceledError" ||
        readString(item, "code") === "ABORT_ERR"
      );
    }),
    isDeviceUnsupported: chain.some(
      (item) => readString(item, "name") === "DeviceUnsupportedError",
    ),
    isLivekitConnectionError: chain.some(
      (item) =>
        readString(item, "name") === "ConnectionError" &&
        getLivekitReason(item) !== undefined,
    ),
    isTokenRefreshLogoutError: chain.some(
      (item) => readString(item, "name") === "TokenRefreshLogoutError",
    ),
    isTypeError: chain.some(
      (item) =>
        item instanceof TypeError || readString(item, "name") === "TypeError",
    ),
    isUnsupportedServer: chain.some(
      (item) => readString(item, "name") === "UnsupportedServer",
    ),
  };
}

function collectCauseChain(error: unknown): readonly unknown[] {
  const result: unknown[] = [];
  const visited = new Set<unknown>();
  let current: unknown = error;

  while (
    current !== undefined &&
    current !== null &&
    !visited.has(current) &&
    result.length < 6
  ) {
    result.push(current);
    visited.add(current);
    current = readUnknown(current, "cause");
  }

  return result;
}

function firstString(
  chain: readonly unknown[],
  property: string,
): string | undefined {
  for (const item of chain) {
    const value = readString(item, property);
    if (value !== undefined) return value;
  }
  return undefined;
}

function firstNumber(
  chain: readonly unknown[],
  property: string,
): number | undefined {
  for (const item of chain) {
    const value = readNumber(item, property);
    if (value !== undefined) return value;
  }
  return undefined;
}

function firstMatchingString(
  chain: readonly unknown[],
  property: string,
  acceptedValues: ReadonlySet<string>,
): string | undefined {
  for (const item of chain) {
    const value = readString(item, property)?.toUpperCase();
    if (value !== undefined && acceptedValues.has(value)) return value;
  }
  return undefined;
}

function firstNestedString(
  chain: readonly unknown[],
  parent: string,
  property: string,
): string | undefined {
  for (const item of chain) {
    const value = readString(readUnknown(item, parent), property);
    if (value !== undefined) return value;
  }
  return undefined;
}

function firstLivekitReason(
  chain: readonly unknown[],
): ConnectionErrorReason | undefined {
  for (const item of chain) {
    const reason = getLivekitReason(item);
    if (reason !== undefined) return reason;
  }
  return undefined;
}

function getLivekitReason(error: unknown): ConnectionErrorReason | undefined {
  const reason = readNumber(error, "reason");
  if (
    reason !== undefined &&
    ConnectionErrorReason[reason] !== undefined &&
    readString(error, "name") === "ConnectionError"
  ) {
    return reason as ConnectionError["reason"];
  }
  return undefined;
}

function firstLivekitContext(
  chain: readonly unknown[],
): DisconnectReason | undefined {
  for (const item of chain) {
    if (getLivekitReason(item) === ConnectionErrorReason.LeaveRequest) {
      const context = readNumber(item, "context");
      if (context !== undefined && DisconnectReason[context] !== undefined) {
        return context as DisconnectReason;
      }
    }
  }
  return undefined;
}

function readUnknown(value: unknown, property: string): unknown {
  if (
    (typeof value !== "object" || value === null) &&
    typeof value !== "function"
  ) {
    return undefined;
  }
  try {
    return Reflect.get(value, property);
  } catch {
    return undefined;
  }
}

function readString(value: unknown, property: string): string | undefined {
  const result = readUnknown(value, property);
  return typeof result === "string" ? result : undefined;
}

function readNumber(value: unknown, property: string): number | undefined {
  const result = readUnknown(value, property);
  return typeof result === "number" && Number.isFinite(result)
    ? result
    : undefined;
}

function isRetryableHttpStatus(status: number | undefined): boolean {
  return (
    status !== undefined &&
    (RETRYABLE_HTTP_STATUSES.has(status) || (status >= 500 && status <= 599))
  );
}

function isRetryableLivekitStatus(facts: ErrorFacts): boolean {
  // LiveKit использует status 200 для отдельного сбоя signal handshake.
  return facts.isLivekitConnectionError && facts.httpStatus === 200;
}

function isTransportFailure(facts: ErrorFacts): boolean {
  return (
    facts.isTypeError ||
    facts.name === "ConnectionError" ||
    facts.name === "NetworkError" ||
    facts.name === "TimeoutError" ||
    facts.networkCode !== undefined
  );
}

function normalizeTransportCode(facts: ErrorFacts): string {
  if (facts.networkCode !== undefined) {
    return `network:${normalizeToken(facts.networkCode)}`;
  }
  if (facts.name === "TimeoutError") return "network:timeout";
  if (facts.isTypeError) return "network:type_error";
  return "network:connection_error";
}

function normalizeUnknownCode(facts: ErrorFacts, error: unknown): string {
  if (facts.matrixErrcode !== undefined) {
    return `matrix:${normalizeToken(facts.matrixErrcode)}`;
  }
  if (facts.httpStatus !== undefined) {
    return `http:${facts.httpStatus}`;
  }
  if (facts.code !== undefined) {
    return `error_code:${normalizeToken(facts.code)}`;
  }
  if (facts.name !== undefined) {
    return `unknown:${normalizeToken(facts.name)}`;
  }
  if (error === null) return "unknown:null";
  return `unknown:${typeof error}`;
}

function normalizeLivekitReason(reason: ConnectionErrorReason): string {
  const name = ConnectionErrorReason[reason];
  return normalizeToken(name ?? "unknown");
}

function normalizeDisconnectReason(reason: DisconnectReason): string {
  const name = DisconnectReason[reason];
  return normalizeToken(name ?? "unknown");
}

function normalizeToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9._-]+/g, "_");
}

function classified(
  kind: "retryable" | "wait_for_network",
  originalError: unknown,
  context: FailureContext,
  normalizedCode: string,
  reason: FailureClassificationReason,
): FailureClassification {
  return {
    kind,
    normalizedCode,
    stableSignature: createStableFailureSignature(context, normalizedCode),
    originalError,
    reason,
  };
}

function cancelled(
  originalError: unknown,
  context: FailureContext,
  normalizedCode: string,
  cancellationCode: CallRecoveryCancellationCode,
): FailureClassification {
  return {
    kind: "cancelled",
    normalizedCode,
    stableSignature: createStableFailureSignature(context, normalizedCode),
    originalError,
    reason: "operation_cancelled",
    cancellationCode,
  };
}

function terminal(
  originalError: unknown,
  context: FailureContext,
  normalizedCode: string,
  code: TerminalCallErrorCode,
  reason: FailureClassificationReason,
): FailureClassification {
  const stableSignature = createStableFailureSignature(context, normalizedCode);
  return {
    kind: "terminal",
    normalizedCode,
    stableSignature,
    originalError,
    reason,
    terminalError: {
      code,
      normalizedCode,
      stableSignature,
      originalError,
    },
  };
}
